import type { ReactNode } from "react"
import type React from "react"

import type { TFunction } from "@bounce/i18n"

// An alias for the boilerplate content retrived from our DB
// through the GraphQL bounce API
export type BoilerplateJSON = Record<string, string[]>

type RequiredField = {
  tag: string
  splitter?: "and" | "or" | ","
  amount?: number
}

export type Boilerplate = {
  content: ReactNode[]
  title: string
}

export type FAQ = {
  id: string
  question: string
  answer: string | React.ReactNode
  structuredAnswer?: string | null
  size?: "sm" | "md" | "lg"
}

export type PreFAQ<T> = {
  id: string
  key: string
  question: (interpolations: T) => string
  answer: (interpolations: T, data: Record<string, string>) => string | React.ReactNode
  structuredAnswer?: (interpolations: T, data: Record<string, string>) => string
  requires: RequiredField[]
}

type BuildFaqsOptions<T> = {
  faqs: PreFAQ<T>[]
  data: BoilerplateJSON
  interpolations: T
  t: TFunction
}

export function buildFaqs<T>({ faqs, data, interpolations, t }: BuildFaqsOptions<T>): FAQ[] {
  return faqs
    .filter(faq => hasRequirements(data, faq.requires))
    .map(faq => {
      const prepedData = prepareData({ data, fields: faq.requires, t })

      return {
        id: faq.id,
        question: faq.question(interpolations),
        answer: faq.answer(interpolations, prepedData),
        structuredAnswer: faq.structuredAnswer ? faq.structuredAnswer(interpolations, prepedData) : null,
      }
    })
}

// Confirms the retrieved JSON has the fields used for the interpolations
function hasRequirements(data: BoilerplateJSON, fields: RequiredField[]) {
  return fields.every(({ tag }) => data[tag]?.length)
}

type PrepareDataOptions<T> = {
  data: BoilerplateJSON
  fields: T
  t: TFunction
}

// Builds translated lists of stuff with splitters.
// ie. "Times Square and Empire State Building", "5th Avenue, Chelsea Market, or Apple Store"
// "Forte da Boa Vista, Roman ruins of São Cucufate, ou Almendres Cromlech"
function prepareData<T extends readonly RequiredField[] & { tag: V }[], V extends string>({
  data,
  fields,
  t,
}: PrepareDataOptions<T>): Record<T[number]["tag"], string> {
  return fields.reduce<Record<string, string>>((acc, f) => {
    const field = data[f.tag]
    if (!field) return acc

    const value = generateListTest({ list: field, splitter: f.splitter, amount: f.amount, t })
    if (!value) return acc

    acc[f.tag] = value
    return acc
  }, {})
}

type GenerateListTestOptions = {
  amount?: number
  list: string[]
  splitter?: "and" | "or" | ","
  t: TFunction
}

function generateListTest({ list, splitter = "and", amount = 3, t }: GenerateListTestOptions) {
  const interpolations: Record<number, string> = {}
  list.map((l, i) => (interpolations[i] = l))

  if (list.length === 0) {
    return ""
  }
  if (list.length === 1 || amount === 1) {
    return list[0]
  }
  if (list.length === 2 || amount === 2) {
    if (splitter === "and") {
      return t("content.general.listOfItemsWithAnd_2", "{{0}} and {{1}}", interpolations)
    } else if (splitter === "or") {
      return t("content.general.listOfItemsWithOr_2", "{{0}} or {{1}}", interpolations)
    }
    return t("content.general.listOfItemsWithComma_2", "{{0}}, {{1}}", interpolations)
  }

  if (splitter === "and") {
    return t("content.general.listOfItemsWithAnd_3", "{{0}}, {{1}}, and {{2}}", interpolations)
  } else if (splitter === "or") {
    return t("content.general.listOfItemsWithOr_3", "{{0}}, {{1}}, or {{2}}", interpolations)
  }
  return t("content.general.listOfItemsWithComma_3", "{{0}}, {{1}}, {{2}}", interpolations)
}

// This exists to safeguard destructuring of faq objects
// Without this, typescript wouldn't complain about destructuring of properties
// that are not checked through the 'requires' prop and might not exist
// leading to an error.

type IfAny<T, Y, N> = 0 extends 1 & T ? Y : N
type NeverAny<T> = IfAny<T, never, T>

export type SafeguardFaq<I> = <T extends readonly RequiredField[] & { tag: V }[], V extends string>(arg: {
  id: string
  key: string
  requires: T
  question: (o: I) => string
  answer: (o: I, a: Record<NeverAny<T[number]["tag"]>, string>) => string | React.ReactNode
  structuredAnswer?: (o: I, a: Record<NeverAny<T[number]["tag"]>, string>) => string
}) => typeof arg

// Example:
//
// Build an identity function with safeguard:
// const safeguardCity: SafeguardFaq<{ city: string }> = (arg) => arg

// safeguardCity({
//   id: "lisbon",
//   key: "lisbon",
//   requires: [{ tag: "city" }],
//   question: (o) => `What's your favorite city?`,
//   answer: (o, a) => `My favorite city is ${a.city}`,
//   structuredAnswer: (o, a) => `I love ${a.city}`,
// })
